// Loader for the Full-3D-Mode shape descriptors
// (packages/material-db/data/material-3d.yaml). The file maps a
// material id to a 3D *form* keyword; colour still comes from the
// main material DB. Materials not listed fall back to a per-phase
// default so every material renders as *something* sensible.

import type { MaterialDef } from "./types";
import { parseYaml } from "./yaml";

export type Shape3DKind =
  | "block"
  | "grains"
  | "liquid"
  | "gas"
  | "crystal"
  | "glow";

const VALID: Shape3DKind[] = [
  "block",
  "grains",
  "liquid",
  "gas",
  "crystal",
  "glow",
];

interface RawShape3d {
  id?: unknown;
  shape?: unknown;
}

/** Parse the `shapes3d:` YAML doc into an id → shape-kind map. */
export function parseShapes3dDoc(yaml: string): Map<string, Shape3DKind> {
  const out = new Map<string, Shape3DKind>();
  const root = parseYaml(yaml) as { shapes3d?: RawShape3d[] };
  if (!root || !Array.isArray(root.shapes3d)) return out;
  for (const raw of root.shapes3d) {
    if (!raw || typeof raw !== "object") continue;
    if (typeof raw.id !== "string" || typeof raw.shape !== "string") continue;
    if (!VALID.includes(raw.shape as Shape3DKind)) continue;
    out.set(raw.id, raw.shape as Shape3DKind);
  }
  return out;
}

/** Resolve a material to its 3D shape: explicit entry from the file
 * if present, else a per-phase / per-behaviour default. */
export function shape3dFor(
  def: MaterialDef | null,
  table: Map<string, Shape3DKind>,
): Shape3DKind {
  if (!def) return "block";
  const explicit = table.get(def.id);
  if (explicit) return explicit;
  if (def.behavior === "flame" || def.behavior === "fuel") return "glow";
  switch (def.phase) {
    case "liquid":
      return "liquid";
    case "gas":
      return "gas";
    case "plasma":
      return "glow";
    default:
      // Solids and powders: tell them apart by behaviour so sand-like
      // powders heap as grains while rigid solids stay blocks.
      return def.behavior === "powder" ? "grains" : "block";
  }
}
